import { useState } from 'react';
import { signASP } from '../api/signingApi';

export default function ASPSigningModal({ asp, signerType, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [attestationAccepted, setAttestationAccepted] = useState(false);
  const [signatureName, setSignatureName] = useState('');

  const isBuyer = signerType === 'buyer';

  async function handleSign() {
    if (!attestationAccepted) {
      setError('You must accept the attestation to sign');
      return;
    }

    if (!signatureName.trim()) {
      setError('Please enter your full legal name');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const signatureData = {
        typed_name: signatureName,
        signed_at: new Date().toISOString(),
      };

      const result = await signASP(asp.id, signerType, signatureData, true);

      if (result.success) {
        if (onSuccess) onSuccess(result);
        onClose();
      } else {
        setError(result.message || 'Failed to sign agreement');
      }
    } catch (err) {
      console.error('Error signing agreement:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-lg max-w-2xl w-full p-6 my-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          Sign Agreement for Sale & Purchase
        </h2>

        <div className="space-y-4 mb-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-medium text-blue-900 mb-2">Agreement Summary</h3>
            <div className="text-sm text-blue-800 space-y-1">
              <p><strong>Project:</strong> {asp.agreement_data?.project?.name}</p>
              <p><strong>Unit:</strong> {asp.agreement_data?.unit?.number}</p>
              <p><strong>Purchase Price:</strong> ${asp.purchase_price_nzd?.toLocaleString('en-NZ', { minimumFractionDigits: 2 })}</p>
              <p><strong>Deposit:</strong> ${asp.deposit_amount_nzd?.toLocaleString('en-NZ', { minimumFractionDigits: 2 })}</p>
            </div>
          </div>

          <div className="bg-red-50 border-2 border-red-300 rounded-lg p-6">
            <h3 className="font-bold text-red-900 mb-3">Critical Legal Notice</h3>

            <div className="space-y-3 text-sm text-red-800">
              <p className="font-medium">
                By signing this document, you are entering into a legally binding contract.
              </p>

              {isBuyer && (
                <div className="space-y-2">
                  <p className="font-medium">As the Purchaser, you agree to:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Purchase the property at the stated price</li>
                    <li>Pay the deposit within {asp.deposit_due_days} working days</li>
                    <li>Complete settlement by the sunset date</li>
                    <li>All terms and conditions in the agreement</li>
                  </ul>
                </div>
              )}

              {!isBuyer && (
                <div className="space-y-2">
                  <p className="font-medium">As the Vendor, you agree to:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Sell the property at the stated price</li>
                    <li>Complete construction by the sunset date</li>
                    <li>Transfer title upon settlement</li>
                    <li>All terms and conditions in the agreement</li>
                  </ul>
                </div>
              )}

              <p className="font-medium text-red-900 mt-4">
                If you have not sought independent legal advice, you should do so before signing.
              </p>
            </div>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-3">Digital Signature</h3>
            <p className="text-sm text-gray-700 mb-3">
              Your digital signature will be recorded along with:
            </p>
            <ul className="text-xs text-gray-600 space-y-1 list-disc list-inside">
              <li>Timestamp: {new Date().toLocaleString('en-NZ')}</li>
              <li>IP Address (for verification)</li>
              <li>Document hash (for integrity)</li>
            </ul>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Full Legal Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={signatureName}
              onChange={(e) => setSignatureName(e.target.value)}
              placeholder="Enter your full legal name as it appears on legal documents"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="mt-1 text-xs text-gray-500">
              This will be your legal signature on the agreement
            </p>
          </div>

          <div className="flex items-start p-4 border-2 border-gray-300 rounded-lg bg-white">
            <input
              type="checkbox"
              id="attestation"
              checked={attestationAccepted}
              onChange={(e) => setAttestationAccepted(e.target.checked)}
              className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-0.5"
            />
            <label htmlFor="attestation" className="ml-3 text-sm text-gray-900">
              <strong>I solemnly declare that:</strong>
              <ul className="mt-2 space-y-1 text-gray-700">
                <li>• I have read and understood the entire agreement</li>
                <li>• I agree to be bound by all terms and conditions</li>
                <li>• I understand this is a legally enforceable contract</li>
                <li>• The signature I provide is genuine and authorized</li>
                <li>• I acknowledge the legal consequences of signing</li>
              </ul>
            </label>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <div className="flex justify-between gap-3 pt-4 border-t">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSign}
            disabled={loading || !attestationAccepted || !signatureName.trim()}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium"
          >
            {loading ? 'Signing...' : 'Sign Agreement'}
          </button>
        </div>
      </div>
    </div>
  );
}
